import { get, set, del, entries, createStore } from "idb-keyval";
import { encryptJSON, decryptJSON } from "./crypto";
import { defaultSettings } from "./pages";

const store = createStore("lightword-db", "documents");

export const listDocs = async () =>
  (await entries(store)).map(([, v]) => v).sort((a, b) => b.updatedAt - a.updatedAt);
export const getDoc = (id) => get(id, store);
export const deleteDoc = (id) => del(id, store);

const excerpt = (html) =>
  html.replace(/<span class="ov-ui"[\s\S]*?<\/span><\/span>/g, "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim().slice(0, 140);

export async function readBody(doc, pw) {
  return doc.encrypted ? decryptJSON(doc.cipher, pw) : doc.body;
}

export async function writeDoc(doc, body, pw) {
  const rec = { ...doc, updatedAt: Date.now() };
  if (pw) {
    rec.encrypted = true;
    rec.cipher = await encryptJSON(body, pw);
    rec.excerpt = "";
    delete rec.body;
  } else {
    rec.encrypted = false;
    rec.body = body;
    rec.excerpt = excerpt(body.html);
    delete rec.cipher;
  }
  await set(rec.id, rec, store);
  return rec;
}

export async function createDoc(title, html = "<p><br></p>", settings) {
  const now = Date.now();
  const rec = { id: crypto.randomUUID(), title, createdAt: now, updatedAt: now };
  return writeDoc(rec, { html, settings: settings || defaultSettings() }, null);
}

export async function duplicateDoc(doc) {
  const now = Date.now();
  const rec = { ...doc, id: crypto.randomUUID(), title: `Copy of ${doc.title}`, createdAt: now, updatedAt: now };
  await set(rec.id, rec, store);
  return rec;
}

export async function renameDoc(doc, title) {
  await set(doc.id, { ...doc, title }, store);
}

const AUTOSAVE_KEY = "lw.autosaveMinutes";
export const getAutosaveMinutes = () => {
  const v = localStorage.getItem(AUTOSAVE_KEY);
  return v === null ? 10 : Number(v);
};
export const setAutosaveMinutes = (m) => localStorage.setItem(AUTOSAVE_KEY, String(m));

const sigStore = createStore("lightword-signatures", "items");
const tplStore = createStore("lightword-templates", "items");
const allOf = async (st) => (await entries(st)).map(([, v]) => v).sort((a, b) => b.createdAt - a.createdAt);

export const listSignatures = () => allOf(sigStore);
export async function saveSignature(src, kind, label) {
  if ((await listSignatures()).some((s) => s.src === src)) return null;
  const r = { id: crypto.randomUUID(), src, kind, label, createdAt: Date.now() };
  await set(r.id, r, sigStore);
  return r;
}
export const deleteSignature = (id) => del(id, sigStore);

export const listTemplates = () => allOf(tplStore);
export async function saveTemplate(name, desc, html, settings, category = "General") {
  const r = { id: crypto.randomUUID(), name, desc, html, settings, category, createdAt: Date.now() };
  await set(r.id, r, tplStore);
  return r;
}
export const deleteTemplate = (id) => del(id, tplStore);
export const updateTemplate = (t, patch) => set(t.id, { ...t, ...patch }, tplStore);
export const TEMPLATE_FOLDERS = ["Contracts", "Invoices", "Letters", "Reports", "Forms", "General"];
export const templateFolders = (list) => [...new Set([...list.map((t) => t.category || "General"), ...TEMPLATE_FOLDERS])];

export async function exportBackup() {
  return {
    app: "PaperLite", format: 1, exportedAt: new Date().toISOString(),
    documents: await listDocs(), templates: await listTemplates(), signatures: await listSignatures(),
    autosaveMinutes: getAutosaveMinutes(), folderMeta: ownMeta(),
  };
}

export async function restoreBackup(data) {
  if (data?.app !== "PaperLite" || !Array.isArray(data.documents)) throw new Error("This file is not a PaperLite backup");
  const res = { added: 0, updated: 0, skipped: 0, templates: 0, signatures: 0 };
  for (const d of data.documents) {
    if (!d?.id || typeof d.title !== "string" || !(d.body || d.cipher)) { res.skipped++; continue; }
    d.updatedAt = Number(d.updatedAt) || Date.now();
    d.createdAt = Number(d.createdAt) || d.updatedAt;
    const ex = await getDoc(d.id);
    if (ex && ex.updatedAt >= d.updatedAt) { res.skipped++; continue; }
    await set(d.id, d, store);
    ex ? res.updated++ : res.added++;
  }
  for (const t of data.templates || []) {
    if (!t?.id || !t.html || (await get(t.id, tplStore))) continue;
    await set(t.id, t, tplStore);
    res.templates++;
  }
  for (const s of data.signatures || []) {
    if (!s?.id || !s.src?.startsWith("data:image/") || (await get(s.id, sigStore))) continue;
    await set(s.id, s, sigStore);
    res.signatures++;
  }
  if (data.folderMeta && typeof data.folderMeta === "object") localStorage.setItem(FOLDER_KEY, JSON.stringify({ ...data.folderMeta, ...ownMeta() }));
  return res;
}

const FOLDER_KEY = "lw.folderMeta";
const DEFAULT_META = {
  Contracts: { color: "#7C3AED", icon: "scale" }, Invoices: { color: "#059669", icon: "receipt" }, Letters: { color: "#0F6CBD", icon: "mail" },
  Reports: { color: "#C2410C", icon: "chart" }, Forms: { color: "#0E7490", icon: "clipboard" }, General: { color: "#64748B", icon: "folder" },
};
const ownMeta = () => JSON.parse(localStorage.getItem(FOLDER_KEY) || "{}");
export const folderMeta = (name) => ({ color: "#64748B", icon: "folder", ...DEFAULT_META[name], ...ownMeta()[name] });
export const setFolderMeta = (name, meta) => localStorage.setItem(FOLDER_KEY, JSON.stringify({ ...ownMeta(), [name]: meta }));
